import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { concat, get, intersectionWith } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import type { IProduct } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { mapProducts } from './helper';

export const useGetProducts = (
  productFilters: string[] = [],
  productVariationFilters: string[] = [],
  overrideQuerySetting?: UseQueryOptions<IProduct[]>,
) => {
  return useQuery({
    queryKey: queryKeys.products.all(
      concat(productFilters, productVariationFilters),
    ),
    queryFn: () => fetchProducts(productFilters, productVariationFilters),
    staleTime: Infinity,
    ...overrideQuerySetting,
  });
};

const DEFAULT_VARIATION_INCLUDES = [
  'products',
  'productVariationSizes',
  'productVariationFlavors',
  'assets',
];

export const useGetAllProducts = (
  productFilters: string[] = [],
  productVariationFilters: string[] = [],
  overrideQuerySetting?: UseQueryOptions<IProduct[]>,
  variationIncludes?: string[],
) => {
  const includes = variationIncludes ?? DEFAULT_VARIATION_INCLUDES;

  return useQuery({
    queryKey: queryKeys.products.all(
      concat(
        productFilters,
        productVariationFilters,
        variationIncludes !== undefined
          ? [
              includes.length === 0
                ? 'noVariationIncludes'
                : `vi:${includes.join(',')}`,
            ]
          : [],
      ),
    ),
    queryFn: () =>
      fetchProducts(productFilters, productVariationFilters, true, includes),
    staleTime: Infinity,
    ...overrideQuerySetting,
  });
};

export const fetchProducts = async (
  productFilters: string[] = [],
  productVariationFilters: string[] = [],
  overrideItersectWith?: boolean,
  variationIncludes: string[] = DEFAULT_VARIATION_INCLUDES,
) => {
  const productUrl = `/products${generateQuery(
    ['productCategories', 'assets'],
    concat(productFilters, 'pageSize=0'),
  )}`;

  const productVariationUrl = `/productVariations${generateQuery(
    variationIncludes,
    concat(productVariationFilters, 'pageSize=0'),
  )}`;

  const variationCacheKey = [
    ...productVariationFilters,
    ...(variationIncludes.length > 0
      ? [`include=${variationIncludes.join(',')}`]
      : []),
    'pageSize=0',
  ];

  const [productsResponse, productVariationsResponse] = await Promise.all([
    requestCCApi({ url: productUrl }),
    queryClient.fetchQuery({
      queryKey: queryKeys.productVariations.all(variationCacheKey),
      queryFn: () => requestCCApi({ url: productVariationUrl }),
      staleTime: Infinity,
    }),
  ]);

  let products = intersectionWith<IProduct, IProduct>(
    productsResponse.data,
    get(productVariationsResponse, 'includes.products.data', []),
    (productA, productB) => {
      return productA.product_code === productB.product_code;
    },
  );

  if (overrideItersectWith) {
    products = productsResponse.data;
  }

  return mapProducts(
    products,
    productVariationsResponse,
    get(productsResponse, 'includes.productCategories.data', []),
    get(productsResponse, 'includes.assets.data', []),
  );
};
